/**
 * Every URL that points at a file the product stores — W3.
 *
 * WHY THIS EXISTS. The API hands out FILE NAMES, not URLs: `BannerName` is
 * `cryptography.png`, a chapter is a `PathMap` under its category's `PathMap`, a question
 * image is a bare guid. The directory each one lives in is server knowledge, and a screen
 * that composes its own path is guessing. That guess fails QUIETLY — a broken image or a
 * video that will not start, with a 404 the user never sees and no error anywhere.
 *
 * It already went wrong once here: the categories service was written with
 * `/root/Content/Banners/{name}`, which is not where banners are. They are in
 * `/root/Courses/CourseBanner/`.
 *
 * WHERE THESE VALUES COME FROM. Not from the constants alone. `SettingsKeys` calls them
 * "relative to wwwroot" —
 *
 *     CourseBannerDirectory  = "Courses/CourseBanner"
 *     ContentArabicDirectory = "Content/Arabic"
 *     ContentEnglishDirectory= "Content/English"
 *     QuestionImagesDirectory= "Content/Questions"
 *
 * — but every URL in the product's own views, and `StorageBroker`'s own path composer
 * (`Path.Combine(WebRootPath, "root", path)`), put `root` in front of them. The files on
 * disk agree: `wwwroot/root/Courses/CourseBanner`, `wwwroot/root/Content/Arabic/{pathMap}`.
 * So the constants are the tail of the path, and `root` is the head. See
 * `API_FINDINGS.md` §30 for the one place server-side that disagrees.
 *
 * AUTHENTICATION: there is none. `/root/**` is served by `app.UseStaticFiles()` with no
 * guard, so a video plays without a token and nothing here needs to sign a URL — and
 * anyone holding one can read it, signed in or not (§31).
 */

/** Everything the product stores hangs off this. */
const ROOT = '/root'

/** The tails, exactly as `SettingsKeys` spells them. */
const DIRECTORIES = {
  categoryBanner: 'Courses/CourseBanner',
  contentArabic: 'Content/Arabic',
  contentEnglish: 'Content/English',
  questionImages: 'Content/Questions',
  scorm: 'Content/Scorm',
  awarenessAttachments: 'AwarenessesAttachments',
  awarenessVideos: 'AwarenessesVideos',
  securityReportAttachments: 'SecurityReportsAttachments',
  certificateTemplates: 'CTemplate',
  userManual: 'UserManual',
  images: 'Images',
}

/**
 * Joins path segments into a URL under `/root`, dropping empties and encoding each part.
 *
 * A missing segment returns `null` rather than a URL with a hole in it: `/root/Content//x`
 * is a 404 that looks like a path, and an `<img>` pointed at one shows a broken icon
 * instead of nothing.
 */
function mediaUrl(directory, ...segments) {
  if (!directory) return null
  if (segments.some((segment) => segment == null || segment === '')) return null

  const tail = segments.map((segment) => encodeURIComponent(String(segment))).join('/')
  return `${ROOT}/${directory}/${tail}`
}

/** A topic's banner image. `BannerName` is a file name, e.g. `cryptography.png`. */
export const categoryBannerUrl = (bannerName) =>
  mediaUrl(DIRECTORIES.categoryBanner, bannerName)

/**
 * A chapter's video, per language.
 *
 * Two path maps, not ids: the category's `PathMap` names the folder and the chapter's names
 * the file. Both come from the server; neither is derivable from an id.
 *
 * @param {'ar'|'en'} language
 */
export function chapterVideoUrl(language, categoryPathMap, chapterPathMap) {
  const directory = language === 'en' ? DIRECTORIES.contentEnglish : DIRECTORIES.contentArabic
  if (!chapterPathMap) return null

  return mediaUrl(directory, categoryPathMap, `${chapterPathMap}.mp4`)
}

/** A question's image, per language. Stored as a bare guid with a `.png` already on it. */
export const questionImageUrl = (fileName) => mediaUrl(DIRECTORIES.questionImages, fileName)

/**
 * A SCORM package's entry point, for the iframe that hosts it.
 *
 * `SCORMPathToIndex` is a relative path INSIDE the package and may contain slashes, so it
 * is the one value here not encoded as a single segment.
 */
export function scormUrl(courseId, pathToIndex) {
  if (!courseId || !pathToIndex) return null
  return `${ROOT}/${DIRECTORIES.scorm}/${encodeURIComponent(courseId)}/${pathToIndex}`
}

export const awarenessAttachmentUrl = (fileName) =>
  mediaUrl(DIRECTORIES.awarenessAttachments, fileName)

export const awarenessVideoUrl = (fileName) => mediaUrl(DIRECTORIES.awarenessVideos, fileName)

export const securityReportAttachmentUrl = (fileName) =>
  mediaUrl(DIRECTORIES.securityReportAttachments, fileName)

export const certificateTemplateUrl = (fileName) =>
  mediaUrl(DIRECTORIES.certificateTemplates, fileName)

/**
 * The user manual, which is a PDF or a video depending on a setting — the caller decides
 * which, because only it knows the setting.
 */
export const userManualUrl = (fileName) => mediaUrl(DIRECTORIES.userManual, fileName)

/** A static image shipped with the product, not uploaded content. */
export const imageUrl = (fileName) => mediaUrl(DIRECTORIES.images, fileName)
